import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { FormField, form } from '@angular/forms/signals';
import { forkJoin, Subscription, Subject, debounceTime, take } from 'rxjs';
import { ProductShipmentService } from '../../../../services/product-shipment/product-shipment.service';
import { DeliveryAddressResponseDto, ProductShipmentDto, ProductShipmentStatus, UpdateProductShipmentDto } from '../../../../common/general-types/briefs.dto';
import { DatePipe } from '@angular/common';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { ImageService } from '../../../../services/image/image.service';

export interface CarrierConfig {
  name: string;
  short: string;
  bg: string;
  fg: string;
}

export const CARRIERS: Record<string, CarrierConfig> = {
  dhl:        { name: 'DHL',         short: 'DHL',  bg: '#FFCC00', fg: '#D40511' },
  ups:        { name: 'UPS',         short: 'UPS',  bg: '#351C15', fg: '#FFB500' },
  fedex:      { name: 'FedEx',       short: 'FX',   bg: '#4D148C', fg: '#ffffff' },
  royalmail:  { name: 'Royal Mail',  short: 'RM',   bg: '#BA1F2D', fg: '#ffffff' },
  usps:       { name: 'USPS',        short: 'USP',  bg: '#004B87', fg: '#ffffff' },
  dpd:        { name: 'DPD',         short: 'DPD',  bg: '#DC0032', fg: '#ffffff' },
  japanpost:  { name: 'Japan Post',  short: 'JP',   bg: '#E60012', fg: '#ffffff' },
  canadapost: { name: 'Canada Post', short: 'CA',   bg: '#EE2722', fg: '#ffffff' },
  auspost:    { name: 'Aus Post',    short: 'AU',   bg: '#DC1928', fg: '#ffffff' },
  evri:       { name: 'Evri',        short: 'Evri', bg: '#190B45', fg: '#FF6F0F' },
};

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #DB24BF, #8C167A)',
  'linear-gradient(135deg, #FF7AB6, #DB24BF)',
  'linear-gradient(135deg, #8303F8, #DB24BF)',
  'linear-gradient(135deg, #FFB37A, #DB24BF)',
  'linear-gradient(135deg, #5E2BFF, #DB24BF)',
  'linear-gradient(135deg, #DB24BF, #FFD166)',
  'linear-gradient(135deg, #B284FD, #DB24BF)',
  'linear-gradient(135deg, #DB24BF, #6508FB)',
  'linear-gradient(135deg, #FF5E8E, #8C167A)',
  'linear-gradient(135deg, #FF8B5C, #DB24BF)',
  'linear-gradient(135deg, #DB24BF, #4D148C)',
  'linear-gradient(135deg, #95159E, #6508FB)',
];

@Component({
  selector: 'mbt-logistics',
  imports: [FormField, DatePipe],
  templateUrl: './logistics.html',
  styleUrl: './logistics.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Logistics {

  private subscriptions = new Subscription();
  private searchQuerySubject = new Subject<string>();

  searchQueryModel = signal({ query: '' });
  searchQueryForm = form(this.searchQueryModel);

  shipments = signal<ProductShipmentDto[]>([]);
  isLoading = signal(false);
  activeStatusFilter = signal<ProductShipmentStatus | null>(null);
  selected = signal<Set<string>>(new Set());
  statusMenuOpenId = signal<string | null>(null);
  copiedId = signal<string | null>(null);
  copiedAddressId = signal<string | null>(null);
  bulkSaving = signal(false);
  carrierMenuOpenId = signal<string | null>(null);
  customCarrierDraft = signal<string>('');
  trackingEditId = signal<string | null>(null);
  trackingDraft = signal<string>('');

  readonly statusOptions: ProductShipmentStatus[] = ['Not-Shipped', 'Shipped', 'Delivered'];
  readonly carrierKeys = Object.keys(CARRIERS);
  readonly carriers = CARRIERS;

  displayedShipments = computed(() => {
    const filter = this.activeStatusFilter();
    return filter ? this.shipments().filter((s) => s.status === filter) : this.shipments();
  });

  highlightedShipmentId = computed(() => {
    const appId = this.manageBriefTabsService.logisticsHighlightApplicationId();
    if (!appId) return null;
    return this.shipments().find((s) => s.briefApplicationId === appId)?.id ?? null;
  });

  statusCounts = computed(() => {
    const all = this.shipments();
    return {
      'Not-Shipped': all.filter((s) => s.status === 'Not-Shipped').length,
      'Shipped': all.filter((s) => s.status === 'Shipped').length,
      'Delivered': all.filter((s) => s.status === 'Delivered').length,
    };
  });

  /** Services */
  public manageBriefTabsService = inject(ManageBriefTabsService);
  private productShipmentService = inject(ProductShipmentService);
  private toastService = inject(ToastNotificationService);
  public imageService = inject(ImageService);
  /** Services - END */

  constructor() {
    this.subscriptions.add(
      this.searchQuerySubject.pipe(debounceTime(350)).subscribe(() => {
        console.log('searchQuerySubject -> query: ', this.searchQueryModel().query);
        if (this.searchQueryModel().query.trim().length > 1) {
          // this.loadShipments();
          console.log('loadShipments() called');
        }
      })
    );

    effect(() => {
      const query = this.searchQueryModel().query;
      this.searchQuerySubject.next(query);
    });

    effect(() => {
      if (this.manageBriefTabsService.focussedBrief()) {
        this.loadShipments();
      }
    });

    effect(() => {
      const shipmentId = this.highlightedShipmentId();
      if (!shipmentId) return;
      setTimeout(() => {
        document.getElementById('logi-row-' + shipmentId)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      setTimeout(() => {
        this.manageBriefTabsService.logisticsHighlightApplicationId.set(null);
      }, 3500);
    });
  }

  loadShipments(): void {
    const brief = this.manageBriefTabsService.focussedBrief();
    if (!brief) return;

    this.isLoading.set(true);
    this.productShipmentService
      .getByBrief(brief.id, null, this.searchQueryModel().query)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.shipments.set(data);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.toastService.alert({ title: 'Error', message: 'Failed to load shipments', type: 'error', icon: 'error' });
        },
      });
  }

  setStatusFilter(status: ProductShipmentStatus | null): void {
    this.activeStatusFilter.set(status);
    this.selected.set(new Set());
  }

  getShipmentFilterButtonClass(filter: ProductShipmentStatus | null): string {
    return this.activeStatusFilter() === filter
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'bg-transparent text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  toggleSelect(id: string): void {
    this.selected.update((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  toggleAll(): void {
    const displayed = this.displayedShipments();
    const sel = this.selected();
    const allDisplayedSelected = displayed.every((s) => sel.has(s.id));
    if (allDisplayedSelected) {
      this.selected.set(new Set());
    } else {
      this.selected.set(new Set(displayed.map((s) => s.id)));
    }
  }

  allSelected(): boolean {
    const displayed = this.displayedShipments();
    return displayed.length > 0 && displayed.every((s) => this.selected().has(s.id));
  }

  someSelected(): boolean {
    const displayed = this.displayedShipments();
    return displayed.some((s) => this.selected().has(s.id)) && !this.allSelected();
  }

  clearSelection(): void {
    this.selected.set(new Set());
  }

  bulkMark(status: ProductShipmentStatus): void {
    const ids = Array.from(this.selected());
    if (!ids.length) return;

    this.bulkSaving.set(true);
    const calls = ids.map((id) => this.productShipmentService.update(id, { status }));
    forkJoin(calls).pipe(take(1)).subscribe({
      next: (updated) => {
        this.shipments.update((list) =>
          list.map((s) => {
            const u = updated.find((r) => r.id === s.id);
            return u ?? s;
          })
        );
        this.selected.set(new Set());
        this.bulkSaving.set(false);
        this.toastService.alert({ title: 'Done', message: `${ids.length} shipment(s) marked as ${status}`, type: 'success', icon: 'success' });
      },
      error: () => {
        this.bulkSaving.set(false);
        this.toastService.alert({ title: 'Error', message: 'Bulk update failed', type: 'error', icon: 'error' });
      },
    });
  }

  openStatusMenu(id: string): void {
    this.statusMenuOpenId.set(this.statusMenuOpenId() === id ? null : id);
  }

  closeStatusMenu(): void {
    this.statusMenuOpenId.set(null);
  }

  changeStatus(shipment: ProductShipmentDto, status: ProductShipmentStatus): void {
    this.closeStatusMenu();
    if (shipment.status === status) return;

    this.productShipmentService.update(shipment.id, { status }).pipe(take(1)).subscribe({
      next: (updated) => {
        this.shipments.update((list) => list.map((s) => (s.id === updated.id ? updated : s)));
      },
      error: (err: { error?: { message?: string } }) => {
        const message = err?.error?.message ?? 'Failed to update status';
        this.toastService.alert({ title: 'Error', message, type: 'error', icon: 'error' });
      },
    });
  }

  copyTracking(shipment: ProductShipmentDto): void {
    if (!shipment.trackingNumber) return;
    navigator.clipboard?.writeText(shipment.trackingNumber.replace(/\s/g, ''));
    this.copiedId.set(shipment.id);
    setTimeout(() => this.copiedId.set(null), 1500);
  }

  openCarrierMenu(id: string): void {
    this.carrierMenuOpenId.set(this.carrierMenuOpenId() === id ? null : id);
    this.customCarrierDraft.set('');
  }

  closeCarrierMenu(): void { this.carrierMenuOpenId.set(null); }

  selectCarrier(shipment: ProductShipmentDto, key: string): void {
    this.closeCarrierMenu();
    if (shipment.carrier === key) return;
    this.productShipmentService.update(shipment.id, { carrier: key }).pipe(take(1)).subscribe({
      next: (updated) => this.shipments.update((list) => list.map((s) => (s.id === updated.id ? updated : s))),
      error: () => this.toastService.alert({ title: 'Error', message: 'Failed to update carrier', type: 'error', icon: 'error' }),
    });
  }

  saveCustomCarrier(shipment: ProductShipmentDto): void {
    const carrier = this.customCarrierDraft().trim();
    if (!carrier) return;
    this.selectCarrier(shipment, carrier);
  }

  setCustomCarrierDraft(e: Event): void {
    this.customCarrierDraft.set((e.target as HTMLInputElement).value);
  }

  openTrackingEdit(shipment: ProductShipmentDto): void {
    this.trackingEditId.set(shipment.id);
    this.trackingDraft.set(shipment.trackingNumber ?? '');
  }

  closeTrackingEdit(): void {
    this.trackingEditId.set(null);
    this.trackingDraft.set('');
  }

  saveTrackingEdit(shipment: ProductShipmentDto): void {
    const trackingNumber = this.trackingDraft().trim();
    this.productShipmentService.update(shipment.id, { trackingNumber }).pipe(take(1)).subscribe({
      next: (updated) => {
        this.shipments.update((list) => list.map((s) => (s.id === updated.id ? updated : s)));
        this.closeTrackingEdit();
      },
      error: () => this.toastService.alert({ title: 'Error', message: 'Failed to save tracking number', type: 'error', icon: 'error' }),
    });
  }

  setTrackingDraft(e: Event): void {
    this.trackingDraft.set((e.target as HTMLInputElement).value);
  }

  copyAddress(shipment: ProductShipmentDto): void {
    const addr = this.creatorAddress(shipment);
    if (!addr) return;
    const lines = [addr.fullName, addr.addressLine1, addr.addressLine2, addr.town, addr.postcode, addr.country]
      .filter(Boolean)
      .join('\n');
    navigator.clipboard?.writeText(lines);
    this.copiedAddressId.set(shipment.id);
    setTimeout(() => this.copiedAddressId.set(null), 1500);
  }

  creatorName(shipment: ProductShipmentDto): string {
    const creator = shipment.briefApplication?.creator;
    if (!creator) return '—';
    return `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim() || '—';
  }

  creatorInitials(shipment: ProductShipmentDto): string {
    const name = this.creatorName(shipment);
    return name === '—' ? '?' : name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  }

  creatorPhoto(shipment: ProductShipmentDto): string | null {
    return shipment.briefApplication?.creator?.photoURL ?? null;
  }

  creatorAvatarGradient(shipment: ProductShipmentDto): string {
    const uid = shipment.briefApplication?.creator?.blazeFirebaseUid ?? '';
    const index = uid.charCodeAt(0) % AVATAR_GRADIENTS.length;
    return AVATAR_GRADIENTS[index];
  }

  creatorHandle(shipment: ProductShipmentDto): string {
    return shipment.briefApplication?.creator?.['instaHandle']
      ? `@${shipment.briefApplication.creator['instaHandle']}`
      : '';
  }

  creatorAddress(shipment: ProductShipmentDto): DeliveryAddressResponseDto | null {
    const addresses = shipment.briefApplication?.creator?.deliveryAddresses;
    if (!addresses?.length) return null;
    return addresses.find((a) => a.isSelectedAddress) ?? addresses[0];
  }

  carrierConfig(carrier: string | null): CarrierConfig | null {
    if (!carrier) return null;
    return CARRIERS[carrier.toLowerCase()] ?? { name: carrier, short: carrier.slice(0, 3).toUpperCase(), bg: '#e5e7eb', fg: '#374151' };
  }

  pillClass(status: ProductShipmentStatus): string {
    switch (status) {
      case 'Shipped': return 'status-pill shipped';
      case 'Delivered': return 'status-pill delivered';
      default: return 'status-pill not-shipped';
    }
  }

  dotClass(status: ProductShipmentStatus): string {
    switch (status) {
      case 'Shipped': return 'status-dot shipped';
      case 'Delivered': return 'status-dot delivered';
      default: return 'status-dot not-shipped';
    }
  }

  pillLabel(status: ProductShipmentStatus): string {
    switch (status) {
      case 'Shipped': return 'Shipped';
      case 'Delivered': return 'Delivered';
      default: return 'Not Shipped';
    }
  }
}
